import './FolderNavigator.css';

import React, { useEffect, useState } from 'react';
import LogbookBreadcrumbs from './LogbookBreadcrumbs';
import FolderItemForm from './folders/FolderItemForm';
import B2BElementsExplorerInMemory from '../../b2b-common/B2BElementsExplorerInMemory';
import FolderCard from './folders/FolderCard';
import { deleteFolder, deleteItem, downloadFileRelatedToItem } from '../../../services/b2b-logbook-apiServices';
import { useLoginContext } from '../../../App';
import { useContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import FileItemForm from './files/FileItemForm';
import NoteItemForm from './notes/NoteItemForm';
import ItemCardFactory from './ItemCardFactory';
import RichNote from './notes/RichNote';
import LinkItemForm from './links/LinkItemForm';
import ConfirmationDialog from '../../b2b-common/ConfirmationDialog';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

const FolderNavigator = ({ logbook, folder = null, onFolderChange, onUpdatedLogbook, onDeleteLogbook }) => {

    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-logbook');

    const [currentLogbook, setCurrentLogbook] = useState(logbook);
    const [currentFolder, setCurrentFolder] = useState(folder);
    const [panelIsOpen, setPanelIsOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [sortedItems, setSortedItems] = useState([]);
    const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);
    const [showNoteView, setShowNoteView] = useState(false);

    useEffect(() => {
        // console.log('currentFolder:', currentFolder);
        // console.log('currentLogbook:', currentLogbook);
        if (currentFolder) {
            setSortedItems([...currentFolder.items].sort((a, b) => {
                if (a.logBookItemType === b.logBookItemType) {
                    return a.logBookItemName.localeCompare(b.logBookItemName);
                }
                return a.logBookItemType.localeCompare(b.logBookItemType);
            }));
        } else {
            if (currentLogbook) {
                setSortedItems([...currentLogbook.items].sort((a, b) => {
                    if (a.logBookItemType === b.logBookItemType) {
                        return a.logBookItemName.localeCompare(b.logBookItemName);
                    }
                    return a.logBookItemType.localeCompare(b.logBookItemType);
                }));
            }
        }
    }, [currentFolder, currentLogbook]);

    useEffect(() => {
        setCurrentLogbook(logbook);
        setCurrentFolder(folder);
    }, [logbook, folder]);

    const handleNewFolder = () => {
        setPanelIsOpen(true);
        setItemToEdit('folder');
    }

    const handleNewFile = () => {
        setPanelIsOpen(true);
        setItemToEdit('file');
    }

    const handleNewNote = () => {
        setPanelIsOpen(true);
        setItemToEdit('note');
    }

    const handleNewLink = () => {
        setPanelIsOpen(true);
        setItemToEdit('link');
    }

    const handleClosePanel = () => {
        setPanelIsOpen(false);
        setSelectedFolder(null);
        setItemToEdit(null);
        setSelectedItem(null);
    }

    const handleEditFolder = (folder) => {
        // console.log('handleEditFolder', folder);
        setSelectedFolder(folder);
        setItemToEdit('folder');
        setPanelIsOpen(true);
    }

    const handleDeleteFolder = async (folder) => {
        // console.log('handleDeleteFolder', folder);
        try {
            const updatedLogbook = await deleteFolder(loginData.accessToken, logbook.logBookId, folder.logBookFolderId);
            showNotification(setNotification, t('Folder deleted successfully.'), 'success', 3000);
            onUpdatedLogbook && onUpdatedLogbook(updatedLogbook);
        } catch (error) {
            console.error('Error deleting folder:', error);
            showNotification(setNotification, t('Error deleting folder: ') + error.response.statusText, 'error', 5000);
        }
    }

    const handleClickFolder = (folder) => {
        // console.log('handleClickFolder', folder);
        setCurrentFolder(folder);
        onFolderChange && onFolderChange(folder);
    }

    const handleFolderChange = async (logBookFolderId) => {
        const folderId = Number(logBookFolderId);
        // console.log('handleFolderChange', folderId);
        if (folderId === 0) {
            setCurrentFolder(null);
            onFolderChange && onFolderChange(null);
        }
    }

    const handleSavedFolder = (updatedLogbook) => {
        onUpdatedLogbook && onUpdatedLogbook(updatedLogbook);
        setPanelIsOpen(false);
        setSelectedFolder(null);
        setItemToEdit(null);
    }

    const handleDeleteItem = async (item) => {
        try {
            // console.log('Item deleted:', item);
            const updatedLogbook = await deleteItem(loginData.accessToken, logbook.logBookId, item.logBookFolderId, item.logBookItemId);
            setCurrentLogbook(updatedLogbook);
            // console.log('updatedLogbook:', updatedLogbook);
            showNotification(setNotification, t('Item deleted successfully.'), 'success', 3000);
            if (item.logBookFolderId !== 0) {
                // console.log('Deleting item from folder:', item);
                const updatedFolder = {
                    ...currentFolder,
                    items: currentFolder.items.filter(i => i.logBookItemId !== item.logBookItemId)
                }
                // console.log('Updated folder:', updatedFolder);
                setCurrentFolder(updatedFolder);
                onFolderChange && onFolderChange(updatedFolder);
            } else {
                setCurrentFolder(null);
            }
            onUpdatedLogbook && onUpdatedLogbook(updatedLogbook);
        } catch (error) {
            console.error('Error deleting item:', error);
            showNotification(setNotification, t('Error deleting item: ') + error.response.statusText, 'error', 5000);
        }
    }

    const handleEditItem = (item) => {
        setSelectedItem(item);
        if (item.logBookItemType === 'NOTE') {
            setItemToEdit('note');
        } else if (item.logBookItemType === 'FILE') {
            setItemToEdit('file');
        } else if (item.logBookItemType === 'LINK') {
            setItemToEdit('link');
        }
        setPanelIsOpen(true);
    }

    const handleClickedItem = (item) => {
        if (item.logBookItemType === 'FILE') {
            setSelectedItem(item);
            setShowDownloadConfirmation(true);
        } else if (item.logBookItemType === 'NOTE') {
            setSelectedItem(item);
            setShowNoteView(true);
        } else if (item.logBookItemType === 'LINK') {
            window.open(item.logBookItemLinkURL, '_blank');
        }
    }

    const handleDownloadItem = async () => {
        console.log('handleDownloadItem', selectedItem);
        try {
            const result = await downloadFileRelatedToItem(loginData.accessToken, logbook.logBookId, selectedItem.logBookFolderId, selectedItem.logBookItemId, selectedItem.logBookItemFileName);
            // const blob = new Blob([file], { type: 'application/octet-stream' });
            // saveAs(blob, item.logBookItemFileName);
            console.log('File downloaded:', result);
        } catch (error) {
            console.error('Error downloading file:', error);
            showNotification(setNotification, t('Error downloading file: ') + error.response.statusText, 'error', 5000);
        } finally {
            setShowDownloadConfirmation(false);
            setSelectedItem(null);
        }
    }

    const handleSavedItem = (updatedItem) => {
        const { logBookFolderId } = updatedItem;
        if (logBookFolderId === 0) {
            const itemExists = currentLogbook.items.find(i => i.logBookItemId === updatedItem.logBookItemId);
            const updatedLogbook = (!itemExists) ? {
                ...currentLogbook,
                items: [...currentLogbook.items, updatedItem]
            } : {
                ...currentLogbook,
                items: currentLogbook.items.map(i => {
                    if (i.logBookItemId === updatedItem.logBookItemId) {
                        return updatedItem;
                    }
                    return i;
                })
            }
            setCurrentLogbook(updatedLogbook);
            onUpdatedLogbook && onUpdatedLogbook(updatedLogbook);
            setSelectedFolder(null);
        } else {
            const itemExists = currentFolder.items.find(i => i.logBookItemId === updatedItem.logBookItemId);
            const updatedLogbook = (!itemExists) ? {
                ...currentLogbook,
                folders: currentLogbook.folders.map(f => {
                    if (f.logBookFolderId === logBookFolderId) {
                        return {
                            ...f,
                            items: [...f.items, updatedItem]
                        }
                    }
                    return f;
                })
            } : {
                ...currentLogbook,
                folders: currentLogbook.folders.map(f => {
                    if (f.logBookFolderId === logBookFolderId) {
                        return {
                            ...f,
                            items: f.items.map(i => {
                                if (i.logBookItemId === updatedItem.logBookItemId) {
                                    return updatedItem;
                                }
                                return i;
                            })
                        }
                    }
                    return f;
                })
            }
            setCurrentLogbook(updatedLogbook);
            onUpdatedLogbook && onUpdatedLogbook(updatedLogbook);
            const updatedFolder = updatedLogbook.folders.find(f => f.logBookFolderId === logBookFolderId);
            setCurrentFolder(updatedFolder);
            onFolderChange && onFolderChange(updatedFolder);
        }
        setSelectedItem(null);
        setPanelIsOpen(false);
        setItemToEdit(null);
    }

    const sortedFolders = [...logbook.folders].sort((a, b) => a.logBookFolderName.localeCompare(b.logBookFolderName));

    return (
        <div className="folder-navigator-container">
            <LogbookBreadcrumbs logbook={logbook}
                folder={currentFolder}
                onNewFolder={handleNewFolder}
                onFolderChange={handleFolderChange}
                onNewFile={handleNewFile}
                onNewNote={handleNewNote}
                onNewLink={handleNewLink}
                onDeleteLogbook={onDeleteLogbook}
            />
            <div className="folder-navigator">
                <div className={`navigator ${panelIsOpen ? 'panel-open' : ''}`}>
                    {!currentFolder && (
                        <B2BElementsExplorerInMemory
                            elementsData={{ content: sortedFolders }}
                            className='folders-explorer'
                            ElementComponent={FolderCard}
                            elementComponentPropName={'folder'}
                            elementComponentHandlers={{
                                onDelete: handleDeleteFolder,
                                onEdit: handleEditFolder,
                                onClick: handleClickFolder,
                            }}
                            numRows={logbook.folders.length}
                        />
                    )}
                    {currentFolder && (
                        <div className="current-folder-data">
                            <div className="folder-content-description">{currentFolder.logBookFolderDescription}</div>
                        </div>
                    )}
                    {sortedItems && sortedItems.length > 0 && (
                        <B2BElementsExplorerInMemory
                            elementsData={{ content: sortedItems }}
                            className='items-explorer'
                            ElementComponent={ItemCardFactory}
                            elementComponentPropName={'item'}
                            elementComponentHandlers={{
                                onDelete: handleDeleteItem,
                                onEdit: handleEditItem,
                                onClick: handleClickedItem,
                            }}
                            numRows={sortedItems.length}
                        />
                    )}
                </div>
                {panelIsOpen && (
                    <div className={`selection-panel ${panelIsOpen ? 'panel-open' : ''}`}>
                        {panelIsOpen && itemToEdit === 'folder' && (
                            <FolderItemForm logbook={logbook} folder={selectedFolder} onClose={handleClosePanel} onSaved={handleSavedFolder} />
                        )}
                        {panelIsOpen && itemToEdit === 'file' && (
                            <FileItemForm logbook={logbook} itemFile={selectedItem} folder={currentFolder} onClose={handleClosePanel} onSaved={handleSavedItem} />
                        )}
                        {panelIsOpen && itemToEdit === 'note' && (
                            <NoteItemForm logbook={logbook} itemNote={selectedItem} folder={currentFolder} onClose={handleClosePanel} onSaved={handleSavedItem} />
                        )}
                        {panelIsOpen && itemToEdit === 'link' && (
                            <LinkItemForm logbook={logbook} itemLink={selectedItem} folder={currentFolder} onClose={handleClosePanel} onSaved={handleSavedItem} />
                        )}
                    </div>
                )}
            </div>
            <ConfirmationDialog
                title={t('Download Confirmation Request')}
                mainText={t('Please confirm you want to download this item?')}
                open={showDownloadConfirmation}
                onClose={() => setShowDownloadConfirmation(false)}
                onConfirm={handleDownloadItem}
            />
            <RichNote itemNote={selectedItem} showNote={showNoteView} onclose={() => setShowNoteView(false)} />
        </div>
    );
}

export default FolderNavigator;